/**
 * Cart API Component
 * Handles add, update, remove, and re-rendering the cart drawer and page.
 *
 * ============================================================
 * REWRITTEN 16 SEP 2026 — SECTION RENDERING API
 * ============================================================
 *
 * WHAT THIS FILE USED TO DO
 *
 * refreshCartDrawer() and refreshCartPage() each built the entire
 * cart markup as a JavaScript template string. Combined with
 * snippets/cart-line-item.liquid that made three copies of a cart
 * line and three copies of the summary, in two languages, that all
 * had to be edited together. They drifted, and a styling change had
 * to be done in CSS alone to avoid touching all three.
 *
 * WHAT IT DOES NOW
 *
 * It asks Shopify to render the Liquid and swaps the result in:
 *
 *     GET /?section_id=cart-drawer      the drawer
 *     GET /cart?section_id=main         the /cart page
 *
 * Note the second one. The cart page's section id is "main", not
 * "cart", because templates/cart.json names it that way. Using the
 * filename there returns nothing and fails silently.
 *
 * Liquid is now the only place cart markup exists. Nothing in this
 * file needs editing when the cart design changes.
 *
 * WHAT IS DELIBERATELY NOT SWAPPED
 *
 * Only [data-cart-body] inside the drawer. The overlay, aside and
 * header stay, because assets/cart-drawer.js binds open and close to
 * those nodes once at init; replacing them kills the close button.
 *
 * GONE FROM THIS FILE
 *
 * updateFreeShippingBar(). The bar used to sit outside the replaced
 * markup and have its numbers patched by hand here. It is inside the
 * refreshed region now, so Liquid renders it correct every time and
 * the two can no longer disagree.
 */
(function () {
  "use strict";

  /* Section ids. See the note above about "main". */
  const DRAWER_SECTION = "cart-drawer";
  const CART_PAGE_SECTION = "main";

  const CartAPI = {
    async addItem(variantId, quantity = 1, options = {}) {
      try {
        const payload = { id: variantId, quantity: quantity };

        if (options && typeof options === "object") {
          if (options.selling_plan) {
            payload.selling_plan = options.selling_plan;
          }
          if (
            options.properties &&
            typeof options.properties === "object" &&
            Object.keys(options.properties).length > 0
          ) {
            payload.properties = options.properties;
          }
        }

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || "Failed to add item to cart");
        }

        const data = await response.json();
        await this.refreshAll();
        return data;
      } catch (error) {
        console.error("Error adding item to cart:", error);
        throw error;
      }
    },

    async updateItem(lineId, quantity) {
      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: lineId, quantity: quantity }),
        });

        if (!response.ok) throw new Error("Failed to update cart item");

        const data = await response.json();
        await this.refreshAll();
        return data;
      } catch (error) {
        console.error("Error updating cart item:", error);
        throw error;
      }
    },

    async removeItem(lineId) {
      return this.updateItem(lineId, 0);
    },

    /**
     * One round trip's worth of refreshing. Callers used to run
     * updateCartCount, refreshCartDrawer and refreshCartPage in
     * sequence, which meant three fetches of the same cart for one
     * button press.
     */
    async refreshAll() {
      await Promise.all([
        this.updateCartCount(),
        this.refreshCartDrawer(),
        this.refreshCartPage(),
      ]);
    },

    async updateCartCount() {
      try {
        const response = await fetch("/cart.js");
        if (!response.ok) return;

        const cart = await response.json();
        const count = cart.item_count || 0;

        /* querySelectorAll, not querySelector. There are two counts
           now: the site header badge and the drawer heading. The old
           singular version updated whichever came first in the DOM
           and left the other stale. */
        document.querySelectorAll("[data-cart-count]").forEach((el) => {
          el.textContent = count;
        });
      } catch (error) {
        console.error("Error updating cart count:", error);
      }
    },

    /**
     * Fetch one section's rendered HTML and return it as a document
     * fragment we can query. Returns null on any failure, and every
     * caller treats null as "leave the current markup alone", so a
     * flaky network shows a stale cart rather than an empty one.
     */
    async fetchSection(url) {
      try {
        const response = await fetch(url, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        if (!response.ok) return null;

        const html = await response.text();
        return new DOMParser().parseFromString(html, "text/html");
      } catch (error) {
        console.error("Error fetching section:", error);
        return null;
      }
    },

    async refreshCartDrawer() {
      const drawer = document.querySelector("[data-cart-drawer]");
      if (!drawer) return;

      const target = drawer.querySelector("[data-cart-body]");
      if (!target) return;

      const doc = await this.fetchSection(
        "/?section_id=" + encodeURIComponent(DRAWER_SECTION)
      );
      if (!doc) return;

      const fresh = doc.querySelector("[data-cart-body]");
      if (!fresh) return;

      target.innerHTML = fresh.innerHTML;
    },

    async refreshCartPage() {
      const container = document.querySelector("[data-cart-page]");
      if (!container) return;

      const doc = await this.fetchSection(
        "/cart?section_id=" + encodeURIComponent(CART_PAGE_SECTION)
      );
      if (!doc) return;

      const fresh = doc.querySelector("[data-cart-page]");
      if (!fresh) return;

      container.innerHTML = fresh.innerHTML;
    },

    formatMoney(cents) {
      const amount = parseInt(cents, 10) / 100;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
    },
  };

  /**
   * Quantity and remove controls.
   *
   * Delegated from document, bound exactly once. The old version
   * queried every button, cloned each one to strip its listeners and
   * rebound them after every refresh. With the markup now being
   * replaced wholesale that dance would have had to run on every
   * change; delegation means it never has to run at all.
   */
  let controlsBound = false;

  function bindCartControls() {
    if (controlsBound) return;
    controlsBound = true;

    document.addEventListener("click", async function (e) {
      if (!e.target || !e.target.closest) return;

      const dec = e.target.closest("[data-cart-decrease]");
      const inc = e.target.closest("[data-cart-increase]");
      const rem = e.target.closest("[data-cart-remove]");
      /* Added 16 Sep 2026 for the empty-cart product cards. It has to be
         delegated like the rest: those cards live inside [data-cart-body],
         which is replaced wholesale on every cart change, so a listener
         bound at page load would be holding a node that no longer exists.
         assets/product-form.js binds with querySelectorAll at init and
         would miss them for exactly that reason. */
      const add = e.target.closest("[data-cart-add]");
      const button = dec || inc || rem || add;
      if (!button) return;

      e.preventDefault();

      /* Guard against a double tap firing two changes against the
         same line before the first one has come back. */
      if (button.dataset.ggBusy === "1") return;
      button.dataset.ggBusy = "1";

      try {
        if (add) {
          const variantId = add.getAttribute("data-cart-add");
          if (!variantId) return;

          /* The label is swapped rather than the button hidden, so the
             card does not change height and the row below it does not
             jump. The node is usually replaced by the refresh before
             this ever needs restoring. */
          const label = add.innerHTML;
          add.classList.add("is-busy");
          add.disabled = true;

          try {
            await CartAPI.addItem(variantId, 1);
          } finally {
            add.disabled = false;
            add.classList.remove("is-busy");
            add.innerHTML = label;
          }
          return;
        }

        if (rem) {
          await CartAPI.removeItem(rem.getAttribute("data-cart-remove"));
          return;
        }

        const current =
          parseInt(button.getAttribute("data-current-quantity"), 10) || 1;

        if (dec) {
          await CartAPI.updateItem(
            dec.getAttribute("data-cart-decrease"),
            Math.max(0, current - 1)
          );
        } else {
          await CartAPI.updateItem(
            inc.getAttribute("data-cart-increase"),
            current + 1
          );
        }
      } catch (error) {
        console.error("Cart control failed:", error);
      } finally {
        /* The node is usually gone by now, replaced by the refresh.
           Clearing the flag matters for the case where the request
           failed and the old button is still on the page. */
        button.dataset.ggBusy = "";
      }
    });
  }

  /* Kept because assets/theme.js and others call it. It is now just
     the delegation switch rather than a rebinding pass. */
  CartAPI.initCartLineItems = bindCartControls;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bindCartControls);
  } else {
    bindCartControls();
  }

  window.CartAPI = CartAPI;
})();